import { Controller } from "@hotwired/stimulus";

/**
 * Automatically submits a form after the element has been visible for a specified duration
 *
 * Usage:
 * <form data-controller="auto-read-notification" data-auto-read-notification-delay-value="5000">
 *   <!-- form content -->
 * </form>
 */
export default class extends Controller {
  static values = {
    delay: { type: Number, default: 1500 }, // Default 3 seconds
  };

  declare delayValue: number;
  private timer: number | null = null;
  private observer: IntersectionObserver | null = null;

  connect() {
    // Create an intersection observer to detect when the notification is visible
    this.observer = new IntersectionObserver(
      (entries) => {
        window.console.debug("Intersection observer entries: %o", entries);
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Element is visible, start the timer
            this.startTimer();
          } else {
            // Element is not visible, clear the timer
            this.clearTimer();
          }
        });
      },
      { threshold: 0.75 } // Consider visible when 75% of the element is in view
    );
    window.console.debug("Intersection observer: %o", this.observer),
      // Start observing the form
      this.observer.observe(this.element);
  }

  disconnect() {
    // Clean up
    window.console.debug("Intersection observer disconnected"),
      this.clearTimer();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private startTimer() {
    window.console.debug("Starting timer"),
      // Clear any existing timer first
      this.clearTimer();

    // Set a new timer
    this.timer = window.setTimeout(() => {
      // Submit the form after the delay
      const form = this.element as HTMLFormElement;
      form.requestSubmit();
    }, this.delayValue);
  }

  private clearTimer() {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
